import { useState, useCallback, useEffect, PropsWithChildren } from 'react';
import { DialogContext } from '../dialog.context';

type RootProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
};

export const Root = ({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
}: PropsWithChildren<RootProps>) => {
  const [uncontrolledOpen, setUncontrolledOpen] =
    useState<boolean>(defaultOpen);
  const [closing, setClosing] = useState<boolean>(false);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const closeWithAnimation = useCallback(() => {
    setClosing(true);

    setTimeout(() => {
      if (isControlled) {
        onOpenChange?.(false);
      } else {
        setUncontrolledOpen(false);
      }

      setClosing(false);
    }, 200);
  }, [isControlled, onOpenChange]);

  const openDialog = useCallback(() => {
    if (isControlled) {
      onOpenChange?.(true);
    } else {
      setUncontrolledOpen(true);
    }
  }, [isControlled, onOpenChange]);

  useEffect(() => {
    if (isControlled && !controlledOpen && closing) {
      setClosing(false);
    }
  }, [controlledOpen, isControlled, closing]);

  return (
    <DialogContext.Provider
      value={{ open, closing, close: closeWithAnimation, openDialog }}
    >
      {children}
    </DialogContext.Provider>
  );
};

Root.displayName = 'Dialog.Root';
